import { useMemo, useRef, useState } from "react";
import { SubmitInspection } from "@/services/Inspections";
import { toKg, toWeightQuantity, validateWeight, WeightInputValue } from "@/utils/weight";
import {
  InspectionContext,
  InspectionContract,
  InspectionDeviationProduct,
  InspectionDeviationType,
  InspectionStreamLine,
} from "./types";
import {
  getContractProductLabel,
  getStreamValue,
  MAX_WEIGHT_KG,
  ORIGINAL_STREAM_KEY,
  PER_ORDER_UNIT_CODE,
} from "./utils";

export type InspectionStep = "weighing" | "deviations" | "review";

export const INSPECTION_STEPS: InspectionStep[] = ["weighing", "deviations", "review"];

/**
 * A number input's value. Mantine gives a number once the input is a valid number, and a string while the
 * field is empty or half-typed ("0,"). The string has to be kept, or the field resets under the user's typing.
 */
export type NumberValue = number | string;

export interface StreamFormValues {
  key: string;
  contractId: number | null;
  /** The number as typed and its unit. The submit body sends it as typed, and the same weight in whole kg. */
  weight: WeightInputValue;
  photos: File[];
}

/** A deviation product: part of the original order's weight that turned out to be one of the company's products. */
export interface DeviationProductFormValues {
  deviationProductId: number | null;
  weightKg: NumberValue;
  photos: File[];
}

/** A deviation service: one of the original contract's deviation services, charged by its unit. */
export interface DeviationServiceFormValues {
  deviationTypeId: number | null;
  registeredNumber: NumberValue;
  photos: File[];
}

export interface InspectionFormValues {
  transportRegistrationNumber: string;
  poNumber: string;
  comment: string;
  multiFraction: boolean;
  /** streams[0] is the original order; the rest are the extra fractions received. */
  streams: StreamFormValues[];
  /** Deviations belong to the original order only; extra fractions never have any. */
  hasDeviation: boolean;
  deviationProducts: DeviationProductFormValues[];
  deviationServices: DeviationServiceFormValues[];
}

export const toNumber = (value: NumberValue) => {
  const number = typeof value === "number" ? value : Number(value.replace(/\s/g, "").replace(",", "."));
  return Number.isNaN(number) ? 0 : number;
};

/** A stream's weight in kg, or 0 while the field is empty or isn't a number. */
export const toStreamKg = (stream: Pick<StreamFormValues, "weight">) => toKg(stream.weight) ?? 0;

/** A "per order" service is counted once, so it has no number to fill in. */
export const isPerOrder = (type: InspectionDeviationType | undefined) => type?.unitCode === PER_ORDER_UNIT_CODE;

/** The deviation service a row has picked, if it's one the order's contract has. */
export const findDeviationType = (
  types: InspectionDeviationType[],
  service: Pick<DeviationServiceFormValues, "deviationTypeId">
) => types.find((type) => type.id === service.deviationTypeId);

/** The deviation product a row has picked, if it's one of the company's active products. */
export const findDeviationProduct = (
  products: InspectionDeviationProduct[],
  row: Pick<DeviationProductFormValues, "deviationProductId">
) => products.find((product) => product.id === row.deviationProductId);

/** What will be submitted: nothing unless "Deviation on receipt" is on. */
const activeProducts = (values: InspectionFormValues) => (values.hasDeviation ? values.deviationProducts : []);
const activeServices = (values: InspectionFormValues) => (values.hasDeviation ? values.deviationServices : []);

export const createInitialValues = (context: InspectionContext): InspectionFormValues => ({
  transportRegistrationNumber: "",
  poNumber: "",
  comment: "",
  multiFraction: false,
  streams: [
    {
      key: ORIGINAL_STREAM_KEY,
      contractId: context.order.contract?.contractId ?? null,
      // Prefilled from the weighing slip or collection weight when there is one, otherwise left to the inspector.
      weight: { amount: context.prefillWeightKg ?? "", unit: "KG" },
      photos: [],
    },
  ],
  hasDeviation: false,
  deviationProducts: [],
  deviationServices: [],
});

export const emptyDeviationProduct = (): DeviationProductFormValues => ({
  deviationProductId: null,
  weightKg: "",
  photos: [],
});

export const emptyDeviationService = (): DeviationServiceFormValues => ({
  deviationTypeId: null,
  registeredNumber: "",
  photos: [],
});

/** A per-order service is sent as 1; the others with the number entered. */
const toRegisteredNumber = (service: DeviationServiceFormValues, deviationTypes: InspectionDeviationType[]) =>
  isPerOrder(findDeviationType(deviationTypes, service)) ? 1 : toNumber(service.registeredNumber);

/**
 * The submit body. streams[0] is the order being inspected; every other stream becomes a new order on its contract.
 * The deviations belong to the original order only.
 */
export const toSubmitInspection = (
  values: InspectionFormValues,
  deviationTypes: InspectionDeviationType[]
): SubmitInspection => {
  const [original, ...additional] = values.streams;
  return {
    finalWeightKg: toStreamKg(original),
    enteredWeight: toWeightQuantity(original.weight),
    photos: original.photos,
    transportRegistrationNumber: values.transportRegistrationNumber.trim() || null,
    poNumber: values.poNumber.trim() || null,
    comment: values.comment.trim() || null,
    products: activeProducts(values).map((product) => ({
      deviationProductId: product.deviationProductId as number,
      weightKg: toNumber(product.weightKg),
      photos: product.photos,
      comment: null,
    })),
    services: activeServices(values).map((service) => ({
      contractDeviationTypeId: service.deviationTypeId as number,
      registeredNumber: toRegisteredNumber(service, deviationTypes),
      photos: service.photos,
      comment: null,
    })),
    additionalStreams: additional.map((stream) => ({
      contractId: stream.contractId as number,
      finalWeightKg: toStreamKg(stream),
      enteredWeight: toWeightQuantity(stream.weight),
      photos: stream.photos,
    })),
  };
};

const isWholeKg = (kg: number) => Number.isInteger(kg) && kg > 0;

/**
 * The body to price the deviations with, or null until there's something complete to price: a weight on the original
 * order and every deviation row filled in apart from its photos. The streams are left out: they have no deviations,
 * and an unfinished stream would only make the preview fail.
 */
export const toPreviewInspection = (
  values: InspectionFormValues,
  context: Pick<InspectionContext, "deviationTypes" | "deviationProducts">
): SubmitInspection | null => {
  const products = activeProducts(values);
  const services = activeServices(values);
  if (!products.length && !services.length) return null;
  if (validateWeight(values.streams[0].weight) !== null) return null;

  const productsComplete = products.every(
    (product) =>
      findDeviationProduct(context.deviationProducts, product) &&
      isWholeKg(toNumber(product.weightKg)) &&
      toNumber(product.weightKg) <= MAX_WEIGHT_KG
  );
  const servicesComplete = services.every((service) => {
    const type = findDeviationType(context.deviationTypes, service);
    return type && (isPerOrder(type) || toNumber(service.registeredNumber) > 0);
  });
  if (!productsComplete || !servicesComplete) return null;

  return { ...toSubmitInspection(values, context.deviationTypes), additionalStreams: [] };
};

/** Updates one row of a list. */
const replaceAt = <T>(list: T[], index: number, patch: Partial<T>) =>
  list.map((item, i) => (i === index ? { ...item, ...patch } : item));

/** Removing the last row of both tables is the same as having no deviation. */
const withoutEmptyDeviation = (values: InspectionFormValues): InspectionFormValues => ({
  ...values,
  hasDeviation: values.hasDeviation && values.deviationProducts.length + values.deviationServices.length > 0,
});

/** The registration form's values and the edits the steps make to them. */
export const useInspectionForm = (context: InspectionContext) => {
  const [values, setValues] = useState<InspectionFormValues>(() => createInitialValues(context));
  // Generated outside the state updaters, which StrictMode runs twice.
  const nextStreamNumber = useRef(1);
  const newStream = (): StreamFormValues => ({
    key: `s${nextStreamNumber.current++}`,
    contractId: null,
    weight: { amount: "", unit: "KG" },
    photos: [],
  });

  const setField = <K extends keyof InspectionFormValues>(field: K, value: InspectionFormValues[K]) =>
    setValues((current) => ({ ...current, [field]: value }));

  const updateStream = (index: number, patch: Partial<StreamFormValues>) =>
    setValues((current) => ({ ...current, streams: replaceAt(current.streams, index, patch) }));

  const addStream = () => {
    const stream = newStream();
    setValues((current) => ({ ...current, streams: [...current.streams, stream] }));
  };

  const removeStream = (index: number) =>
    setValues((current) => ({ ...current, streams: current.streams.filter((_, i) => i !== index) }));

  /** Turning multiple fractions on adds the first extra row; turning it off keeps only the original order. */
  const setMultiFraction = (on: boolean) => {
    const stream = on && context.eligibleContracts.length ? newStream() : null;
    setValues((current) =>
      on
        ? {
            ...current,
            multiFraction: true,
            streams: current.streams.length === 1 && stream ? [...current.streams, stream] : current.streams,
          }
        : { ...current, multiFraction: false, streams: current.streams.slice(0, 1) }
    );
  };

  /** Rows are kept while deviations are off, so turning them back on restores what was entered. */
  const setHasDeviation = (on: boolean) => setValues((current) => ({ ...current, hasDeviation: on }));

  const updateProduct = (index: number, patch: Partial<DeviationProductFormValues>) =>
    setValues((current) => ({ ...current, deviationProducts: replaceAt(current.deviationProducts, index, patch) }));

  const addProduct = () =>
    setValues((current) => ({
      ...current,
      deviationProducts: [...current.deviationProducts, emptyDeviationProduct()],
    }));

  const removeProduct = (index: number) =>
    setValues((current) =>
      withoutEmptyDeviation({
        ...current,
        deviationProducts: current.deviationProducts.filter((_, i) => i !== index),
      })
    );

  const updateService = (index: number, patch: Partial<DeviationServiceFormValues>) =>
    setValues((current) => ({ ...current, deviationServices: replaceAt(current.deviationServices, index, patch) }));

  const addService = () =>
    setValues((current) => ({
      ...current,
      deviationServices: [...current.deviationServices, emptyDeviationService()],
    }));

  const removeService = (index: number) =>
    setValues((current) =>
      withoutEmptyDeviation({
        ...current,
        deviationServices: current.deviationServices.filter((_, i) => i !== index),
      })
    );

  return {
    values,
    setField,
    updateStream,
    addStream,
    removeStream,
    setMultiFraction,
    setHasDeviation,
    updateProduct,
    addProduct,
    removeProduct,
    updateService,
    addService,
    removeService,
  };
};

export type InspectionFormApi = ReturnType<typeof useInspectionForm>;

/** Labels, weights and deviation lines derived from the form, shared by the steps. */
export const useInspectionSummary = (values: InspectionFormValues, context: InspectionContext) =>
  useMemo(() => {
    const contracts = new Map<number, InspectionContract>(
      [...(context.order.contract ? [context.order.contract] : []), ...context.eligibleContracts].map((contract) => [
        contract.contractId,
        contract,
      ])
    );
    const contractFor = (contractId: number | null) => (contractId === null ? undefined : contracts.get(contractId));
    const labelFor = (contract: InspectionContract | undefined) => (contract ? getContractProductLabel(contract) : "");
    // A market order has no contract, so its stream is named by its product.
    const streamLabel = (streamKey: string) =>
      streamKey === ORIGINAL_STREAM_KEY && !context.order.contract
        ? context.order.productName
        : labelFor(contractFor(values.streams.find((stream) => stream.key === streamKey)?.contractId ?? null));

    const typeOf = (service: Pick<DeviationServiceFormValues, "deviationTypeId">) =>
      findDeviationType(context.deviationTypes, service);
    const productOf = (row: Pick<DeviationProductFormValues, "deviationProductId">) =>
      findDeviationProduct(context.deviationProducts, row);

    const streamLines: InspectionStreamLine[] = values.streams.flatMap((stream) => {
      const contract = contractFor(stream.contractId);
      return contract ? [{ key: stream.key, weightKg: toStreamKg(stream), contract }] : [];
    });
    // The fractions' contracts are between the same two companies, so they almost always share a currency.
    const streamCurrencies = [...new Set(streamLines.map((line) => line.contract.currency))];

    const products = activeProducts(values);
    const services = activeServices(values);
    // A market order has no contract, and can't have deviations of either kind.
    const isMarketOrder = !context.order.contract;
    const canAddProducts = !isMarketOrder && context.deviationProducts.length > 0;
    const canAddServices = !isMarketOrder && context.deviationTypes.length > 0;

    return {
      contractFor,
      labelFor,
      streamLabel,
      typeOf,
      productOf,
      /** The original order's product, which the deviation products are taken out of. */
      productName: context.order.productName,
      deviationTypes: context.deviationTypes,
      deviationProducts: context.deviationProducts,
      isMarketOrder,
      /** Products are the company's own, whatever the contract. */
      canAddProducts,
      /** Services are the ones on the order's contract. */
      canAddServices,
      canRegisterDeviations: canAddProducts || canAddServices,
      /** Rows in both tables together, which the backend limits. */
      deviationRowCount: products.length + services.length,
      /** The products that have one picked, for the review. */
      productLines: products.flatMap((row) => {
        const product = productOf(row);
        return product ? [{ product, weightKg: toNumber(row.weightKg) }] : [];
      }),
      /** The services that have one picked, for the review. */
      serviceLines: services.flatMap((service) => {
        const type = typeOf(service);
        return type ? [{ type, registeredNumber: toRegisteredNumber(service, context.deviationTypes) }] : [];
      }),
      totalWeightKg: values.streams.reduce((sum, stream) => sum + toStreamKg(stream), 0),
      /** What the fractions cost at their contracts' prices. Null when they're in different currencies: no total then. */
      streamsTotal:
        streamCurrencies.length === 1
          ? {
              amount: streamLines.reduce((sum, line) => sum + (getStreamValue(line.weightKg, line.contract) ?? 0), 0),
              currency: streamCurrencies[0],
            }
          : null,
      withDeviation: products.length + services.length > 0,
    };
  }, [values, context]);

export type InspectionSummary = ReturnType<typeof useInspectionSummary>;
